import SlingTip from '@sdk/common/slingTip/SlingTip';
import Column from '@sdk/components/ui/spacing/Column';
import Text14 from '@sdk/components/ui/typography/Text14';
import palette from '@sdk/theme/colorsLight';
import gettext from '@sdk/lang/gettext';
import dateToHumanFormat from '@sdk/utils/dateFormat/dateToHumanFormat';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { SUBSCRIPTION_NAMES } from '@sdk/account/constants';

import { TaxInfoTopTip } from '@web/account/billing/EditBilling/TaxInfoTopTip/TaxInfoTopTip';
import { MissingBillingAddressTopTip } from '@web/account/billing/EditBilling/MissingBillingAddressTopTip/MissingBillingAddressTopTip';

import { useEditBillingTopTip } from './editBillingTopTip.hooks';

type Props = {
  isCreditCardExpired: boolean;
  subscriptionLevelToDisplay: number;
  hasTax: boolean;
};

const EditBillingTopTip = ({ isCreditCardExpired, subscriptionLevelToDisplay, hasTax }: Props) => {
  const { billingInfo, isAddressTipVisible, isTaxTipVisible, shouldForceAddressUpdate } =
    useEditBillingTopTip(hasTax);

  if (!billingInfo.subscriptionStartDate && billingInfo.formerSubscriptionLevel) {
    return (
      <SlingTip isVisible={true} backgroundColor={palette.dangerLight} noShadow>
        <Column>
          <Text14 themeColor={ThemeColorType.danger} semiBold>
            {gettext('Your %(subscriptionName)s subscription has been suspended.', {
              subscriptionName: SUBSCRIPTION_NAMES[subscriptionLevelToDisplay],
            })}
          </Text14>
          <Text14 themeColor={ThemeColorType.danger} semiBold>
            {isCreditCardExpired
              ? gettext(
                  'Update your payment details to regain access to the %(subscriptionName)s features.',
                  {
                    subscriptionName: SUBSCRIPTION_NAMES[subscriptionLevelToDisplay],
                  }
                )
              : gettext(
                  'To regain access to the %(subscriptionName)s features, retry payment or add a different payment method.',
                  {
                    subscriptionName: SUBSCRIPTION_NAMES[subscriptionLevelToDisplay],
                  }
                )}
          </Text14>
        </Column>
      </SlingTip>
    );
  }

  if (isCreditCardExpired) {
    return (
      <SlingTip isVisible={true} backgroundColor={palette.dangerLight} noShadow>
        <Column>
          <Text14 themeColor={ThemeColorType.danger} semiBold>
            {gettext('Your credit card has now expired.')}
          </Text14>
          <Text14 themeColor={ThemeColorType.danger} semiBold>
            {gettext('To keep your subscription active, update your payment details.')}
          </Text14>
        </Column>
      </SlingTip>
    );
  }

  if (billingInfo.paymentFailedClientSecret && billingInfo.paymentFailedDate) {
    return (
      <SlingTip isVisible={true} backgroundColor={palette.dangerLight} noShadow>
        <Column>
          <Text14 themeColor={ThemeColorType.danger} semiBold>
            {gettext("We can't get your recurring payment working due to authentication failure.")}
          </Text14>
          <Text14 themeColor={ThemeColorType.danger} semiBold>
            {gettext(
              'Retry payment or update your payment details to keep using the paid features.'
            )}
          </Text14>
        </Column>
      </SlingTip>
    );
  }

  if (isAddressTipVisible) {
    return <MissingBillingAddressTopTip isErrorTip={shouldForceAddressUpdate} />;
  }

  if (isTaxTipVisible) {
    return <TaxInfoTopTip />;
  }

  return (
    <SlingTip isVisible={true}>
      <Text14 semiBold themeColor={ThemeColorType.white}>
        {billingInfo.scheduledToResumeDate
          ? gettext(
              'Your %(subscriptionName)s subscription has been paused. It will resume automatically on %(dateStr)s. You can still access the paid features until the end of the current billing period.',
              {
                subscriptionName: SUBSCRIPTION_NAMES[subscriptionLevelToDisplay],
                dateStr: dateToHumanFormat(billingInfo.scheduledToResumeDate, false, true),
              }
            )
          : gettext(
              'Sling automatically adjusts your invoice, as the number of users on your account changes.'
            )}
      </Text14>
    </SlingTip>
  );
};

export default EditBillingTopTip;
